import { Injectable, inject } from '@angular/core';
import { Observable, of, take, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private toastr = inject(ToastrService);
    private readonly stickyOptions = {
        disableTimeOut: true,
        timeOut: 0,
        extendedTimeOut: 0,
        closeButton: true,
        tapToDismiss: false
    } as const;

    private sanitizeHttpMessage(message: string): string {
        const raw = String(message || '').trim();
        if (!raw) return raw;
        const match = raw.match(/^Http failure response for .*?:\s*(.*)$/i);
        if (!match) return raw;
        const tail = (match[1] || '').trim();
        if (!tail) return 'Request failed. Please try again.';
        if (/^0\s+/i.test(tail) || /unknown error/i.test(tail)) return 'Network error. Please try again.';
        return `Request failed: ${tail}`;
    }

    private resolveMessage(message: unknown, fallback: string): string {
        if (typeof message === 'string' && message.trim()) return this.sanitizeHttpMessage(message);
        if (message && typeof (message as any).message === 'string') return (message as any).message;
        if (message && typeof (message as any).error === 'string') return (message as any).error;
        return fallback;
    }

    showSuccess(message: string | unknown, fallback = 'Success'): Observable<void> {
        return of(void 0).pipe(tap(() => this.toastr.success(this.resolveMessage(message, fallback))));
    }

    showError(message: string | unknown, fallback = 'Something went wrong'): Observable<void> {
        return of(void 0).pipe(tap(() => this.toastr.error(this.resolveMessage(message, fallback))));
    }

    showInfo(message: string | unknown, fallback = 'Notice'): Observable<void> {
        return of(void 0).pipe(tap(() => this.toastr.info(this.resolveMessage(message, fallback))));
    }

    showActionableInfo(message: string | unknown, title: string, onOpen: () => void): Observable<void> {
        return of(void 0).pipe(
            tap(() => {
                const toast = this.toastr.info(this.resolveMessage(message, title), title, {
                    closeButton: true,
                    disableTimeOut: false,
                    extendedTimeOut: 1500,
                    progressBar: true,
                    tapToDismiss: false,
                    timeOut: 9000,
                    toastClass: 'ngx-toastr nibni-action-toast'
                });
                toast.onTap.pipe(take(1)).subscribe(() => onOpen());
            })
        );
    }

    showWarning(message: string | unknown, fallback = 'Warning'): Observable<void> {
        return of(void 0).pipe(tap(() => this.toastr.warning(this.resolveMessage(message, fallback))));
    }

    showWarningWithTimeout(message: string | unknown, timeOutMs = 15000, fallback = 'Warning'): Observable<void> {
        return of(void 0).pipe(
            tap(() =>
                this.toastr.warning(this.resolveMessage(message, fallback), undefined, {
                    closeButton: true,
                    disableTimeOut: false,
                    timeOut: Math.max(0, Number(timeOutMs) || 0),
                    extendedTimeOut: 2000
                })
            )
        );
    }

    showStickyWarning(message: string | unknown, fallback = 'Warning'): Observable<void> {
        return of(void 0).pipe(tap(() => this.toastr.warning(this.resolveMessage(message, fallback), undefined, this.stickyOptions)));
    }

    /**
     * Show a standard "access restricted" toaster with a longer duration so users
     * can read it. Used for 403 / permission-denied scenarios.
     * See 05-STABILIZATION-PLAN.md P0.4 (bug 3e/3d).
     */
    showAccessRestricted(message?: string): Observable<void> {
        const msg = message?.trim() || 'Your access is restricted — contact your administrator for access.';
        return of(void 0).pipe(
            tap(() =>
                this.toastr.warning(msg, undefined, {
                    closeButton: true,
                    disableTimeOut: false,
                    timeOut: 15000,
                    extendedTimeOut: 5000,
                    tapToDismiss: false
                })
            )
        );
    }
}
